import React, { FC, useCallback, useRef, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { $Locale } from '@lemon8/web-app-shared/atom';
import { useClickAway } from '~/utils/click-away';
import cls from 'classnames';
import { Language, SupportedLanguageMetas } from '@lemon8/web-app-shared/i18n';
import { $UserSelectedLanguage } from '~/model';
import ChevronDownFill from './chevron_down_fill.svg';
import { EClickPosition } from '~/utils/click-position';

import './style.scss';

const LanguageSelectorPanelItem = ({ language, displayName }: { language: Language; displayName: string }) => {
  const setSelectedLanguage = useSetAtom($UserSelectedLanguage);

  const [hover, setHover] = useState(false);

  const handleItemClick = useCallback(() => {
    window.Tea('wap_click', {
      current_position: EClickPosition.LANGUAGE_SWITCH,
      page_click_id: `${language}_language`,
      is_cta: 0,
    });
    setSelectedLanguage(language);
  }, [language, setSelectedLanguage]);

  return (
    <li
      className={cls('language_selector_panel_item', { hover })}
      onClick={handleItemClick}
      onPointerEnter={() => {
        setHover(true);
      }}
      onPointerLeave={() => {
        setHover(false);
      }}
    >
      {displayName}
    </li>
  );
};

export const MenuLanguageSelector: FC = () => {
  const [open, setOpen] = useState(false);
  const languageSelector = useRef<HTMLLIElement>(null);
  const { language } = useAtomValue($Locale);

  useClickAway(
    languageSelector,
    useCallback(() => {
      setOpen(false);
    }, []),
  );

  const handlePointerEnter = useCallback((e: React.PointerEvent<HTMLElement>) => {
    if (e.pointerType === 'mouse') {
      setOpen(true);
    }
  }, []);

  const handlePointerLeave = useCallback((e: React.PointerEvent<HTMLElement>) => {
    if (e.pointerType === 'mouse') {
      setOpen(false);
    }
  }, []);

  const handleClick = useCallback(() => {
    setOpen((raw) => !raw);
  }, []);

  return (
    <li
      className="legal_panel_item menu-language-selector"
      ref={languageSelector}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
      onClick={handleClick}
    >
      {SupportedLanguageMetas.find((meta) => meta.language === language)?.displayName}
      <ChevronDownFill />
      <ul className={cls('language_selector_panel', { open })}>
        {SupportedLanguageMetas.map((meta) => (
          <LanguageSelectorPanelItem key={meta.language} language={meta.language} displayName={meta.displayName} />
        ))}
      </ul>
    </li>
  );
};
